import { memo } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectPageValidation } from 'store/slices/pageValidation';
import { selectPartnerSettings } from 'store/slices/partnerSettings';
// Import Hooks
import { useValidator } from 'hooks';
// Import UI components
import { ReportItemsConfig } from './config';
// Import Components
import ReportItem from './ReportItem';

const { enableDropHere } = ReportItemsConfig;

const ReportItems = ({ getRowByIndex, dropAcceptType, firstReport, setFirstReport, reports, setReports }) => {
    const validator = useValidator();
    const pageValidation = useAppSelector(selectPageValidation);
    const { CompositeReportMaxCount } = useAppSelector(selectPartnerSettings) ?? {};

    const mapReportItem = ({ data }) => {
        return {
            title: data.Name,
            type: data.ReportType,
            reportId: data.ReportId,
            enableDropHere: false,
            data,
        };
    };

    const pushDropHereIfNecessary = (tmpReport) => {
        if (!isNil(CompositeReportMaxCount) && tmpReport.length !== CompositeReportMaxCount - 1) {
            const push = tmpReport.reduce((acc, value) => (acc = acc && !value.enableDropHere), true);
            if (push) {
                tmpReport.push({ ...enableDropHere });
            }
        }
    };

    const clearHandler = (isFirst: boolean, index?: number) => {
        return () => {
            if (isFirst) {
                setFirstReport({ ...enableDropHere });
                validator({ ChoosedReportsFirstChild: false, ChoosedReports: false });
            } else {
                setReportsValue(index, false);
            }
        };
    };

    const dropHandler = (isFirst: boolean, index?: number) => {
        return (rowIndex: number) => {
            if (isFirst) {
                setFirstReport(mapReportItem(getRowByIndex(rowIndex)));
                validator({ ChoosedReportsFirstChild: true, ChoosedReports: !reports[0].enableDropHere });
            } else {
                setReportsValue(index, true, rowIndex);
            }
        };
    };

    const setReportsValue = (index: Nilable<number> = undefined, isDrop: boolean, rowIndex?: number) => {
        const tmpReports = [...reports];
        if (isDrop) {
            tmpReports[index ?? ''] = mapReportItem(getRowByIndex(rowIndex));
        } else {
            tmpReports.splice(index ?? 0, 1);
        }
        pushDropHereIfNecessary(tmpReports);
        setReports(tmpReports);
        validator({ ChoosedReports: !tmpReports[0]?.enableDropHere });
    };

    return (
        <div>
            <div className="dropping-container-info-inner">
                <ReportItem
                    dropReport={dropHandler(true)}
                    itemData={firstReport}
                    acceptType={dropAcceptType}
                    onClear={clearHandler(true)}
                    isValid={
                        isNil(pageValidation.ChoosedReportsFirstChild?.isValid) ||
                        pageValidation.ChoosedReportsFirstChild?.isValid
                    }
                />
            </div>
            {reports.map((item, index) => (
                <div className="dropping-container-info-inner" key={index}>
                    <ReportItem
                        key={index}
                        dropReport={dropHandler(false, index)}
                        itemData={item}
                        acceptType={dropAcceptType}
                        onClear={clearHandler(false, index)}
                        isValid={
                            isNil(item?.isValid)
                                ? index !== 0 ||
                                  isNil(pageValidation.ChoosedReports?.isValid) ||
                                  pageValidation.ChoosedReports.isValid
                                : item.isValid
                        }
                    />
                </div>
            ))}
        </div>
    );
};

ReportItems.propTypes = {
    getRowByIndex: PropTypes.func.isRequired,
    firstReport: PropTypes.object.isRequired,
    reports: PropTypes.array.isRequired,
    setFirstReport: PropTypes.func.isRequired,
    setReports: PropTypes.func.isRequired,
    dropAcceptType: PropTypes.string.isRequired,
};

export default memo(ReportItems);
